import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useSectionsWithChannels, SectionWithChannels } from '@/hooks/useSections';
import { useCohorts } from '@/hooks/useCohorts';
import {
  Hash,
  Lock,
  Loader2,
  ChevronDown,
  ChevronRight,
  MapPin,
  Briefcase,
  Handshake,
  Users,
  Sparkles,
  Megaphone,
  HandHelping,
  MessageCircle,
  Newspaper,
  CircleHelp,
  LucideIcon,
} from 'lucide-react';

const COLLAPSED_SECTIONS_KEY = 'qbn-collapsed-sections';

const SECTION_ICON_MAP: Record<string, LucideIcon> = {
  function: Briefcase,
  functions: Briefcase,
  contributing: Handshake,
  contribution: Handshake,
  interest: Sparkles,
  interests: Sparkles,
  cohort: Users,
  cohorts: Users,
  everyone: Users,
  other: CircleHelp,
};

const CHANNEL_ICON_MAP: Record<string, LucideIcon> = {
  announcements: Megaphone,
  intros: HandHelping,
  'q-a': CircleHelp,
  jobs: Briefcase,
  'queer-news': Newspaper,
  general: MessageCircle,
  referrals: Handshake,
};

function getSectionIcon(sectionName: string): LucideIcon {
  const key = sectionName.trim().toLowerCase();
  return SECTION_ICON_MAP[key] || Hash;
}

function getStoredCollapsedState(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function setStoredCollapsedState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

interface SectionGroupProps {
  section: SectionWithChannels;
  isCollapsed: boolean;
  onToggle: () => void;
}

function SectionGroup({ section, isCollapsed, onToggle }: SectionGroupProps) {
  const hasChannels = section.channels.length > 0;
  const SectionIcon = getSectionIcon(section.name);

  return (
    <div className="mb-2">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
      >
        {hasChannels && (
          isCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )
        )}
        {!hasChannels && <span className="w-3" />}
        <SectionIcon className="h-3.5 w-3.5" />
        <span>{section.name}</span>
      </button>

      {!isCollapsed && hasChannels && (
        <div className="space-y-0.5 mt-0.5">
          {section.channels.map((channel) => {
            const ChannelIcon = CHANNEL_ICON_MAP[channel.slug] || Hash;
            const isCohortChannel = !!channel.cohort_id;
            const linkPath = `/channels/${channel.slug}`;

            return (
              <NavLink
                key={channel.id}
                to={linkPath}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ml-2',
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
              >
                {isCohortChannel ? (
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ChannelIcon className="h-4 w-4 flex-shrink-0" />
                )}
                <span className="truncate">{channel.name || channel.slug}</span>
                {channel.is_restricted && (
                  <Lock className="h-3 w-3 ml-auto flex-shrink-0" />
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ChannelNav({ compact = false }: { compact?: boolean }) {
  const { data: sections, isLoading: sectionsLoading } = useSectionsWithChannels();
  const { data: cohorts, isLoading: cohortsLoading } = useCohorts();
  const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>(
    getStoredCollapsedState
  );

  useEffect(() => {
    setStoredCollapsedState(collapsedState);
  }, [collapsedState]);

  const toggleSection = (sectionId: string) => {
    setCollapsedState((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const isLoading = sectionsLoading || cohortsLoading;

  // Build visible sections, handling cohorts specially
  const visibleSections: SectionWithChannels[] = [];

  if (sections) {
    for (const section of sections) {
      const isCohortSection = section.name.toLowerCase() === 'cohorts';

      if (isCohortSection) {
        // For cohorts section: if it has channels with cohort_id, use those
        // Otherwise, fall back to mapping cohorts directly
        const cohortChannels = section.channels.filter(ch => ch.cohort_id);

        if (cohortChannels.length > 0) {
          visibleSections.push({ ...section, channels: cohortChannels });
        } else if (cohorts && cohorts.length > 0) {
          // Fallback: map cohorts to channel-like objects
          visibleSections.push({
            ...section,
            channels: cohorts.map((cohort) => ({
              id: cohort.id,
              name: cohort.name,
              slug: cohort.slug,
              description: cohort.description,
              channel_type: 'discussion' as const,
              section_id: section.id,
              cohort_id: cohort.id,
              is_active: cohort.is_active,
              is_restricted: false,
              sort_order: 0,
              created_at: cohort.created_at,
            })),
          });
        }
      } else if (section.channels.length > 0) {
        visibleSections.push(section);
      }
    }
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (visibleSections.length === 0) {
    return (
      <div className="px-3 py-2 text-sm text-muted-foreground">
        No channels available
      </div>
    );
  }

  // Compact: icon-only view for collapsed sidebar
  if (compact) {
    const allChannels = visibleSections.flatMap((s) => s.channels);
    return (
      <nav className="space-y-1 py-2">
        <div className="h-px bg-border mx-2 mb-2" />
        {allChannels.map((channel) => {
          const isCohort = !!channel.cohort_id;
          const Icon = isCohort
            ? MapPin
            : CHANNEL_ICON_MAP[channel.slug] || Hash;
          return (
            <NavLink
              key={channel.id}
              to={`/channels/${channel.slug}`}
              title={channel.name || channel.slug}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <Icon className="h-5 w-5" />
            </NavLink>
          );
        })}
      </nav>
    );
  }

  return (
    <nav className="space-y-1 py-2">
      {visibleSections.map((section) => (
        <SectionGroup
          key={section.id}
          section={section}
          isCollapsed={collapsedState[section.id] ?? false}
          onToggle={() => toggleSection(section.id)}
        />
      ))}
    </nav>
  );
}
